import { useRef, useLayoutEffect, useState, useEffect, useMemo } from 'react'
import { PostedPost as IPostedPost, IndexedObject } from 'interfaces'
import { ScrollListener } from 'components/ScrollListener'
import { PostGroupHeader } from '../PostGroup'
import { PostedPost } from 'components/Posts'
import { PostedSortBy, BulkPostActions } from 'routes/publishing/components/BulkActionsMenus'
import { BulkPostActionsProps } from 'routes/publishing/components/BulkActionsMenus/BulkPostActions'
import styles from '../PlannedPostsList/PlannedPostsList.pcss'
import { Virtuoso, GroupedVirtuoso, ScrollSeekPlaceholderProps } from 'react-virtuoso'
import { ScrollSeekPlaceholder } from 'components/VirtualizedList'

const SCROLL_TRESHOLD = 350

interface PostGroupData {
  posts: IPostedPost[]
  weekday: string
  postsStartIndex: number
  index?: number
  key?: string
}

export interface PostedPostsListProps {
  posts: IPostedPost[]
  sortBy?: PostedSortBy
  className?: string
  selectedPosts: { [id: string]: boolean } | 'all'
  bulkActionsProps: BulkPostActionsProps
  actionsClassName?: string
  onLoadNextPage: () => void
  onRequeuePosted: (id: string, ppid: string) => void
  onRepost: (post: IPostedPost) => void
  onCopy: (post: IPostedPost) => void
  onMediaPreviewBtnClick: (post: IPostedPost) => void
  onShareNow?: (post: IPostedPost) => void
  onTogglePostSelected: (id: string) => void
}

export function PostedPostsList(props: PostedPostsListProps) {
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)
  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])
  const [groups, setGroups] = useState<Array<PostGroupData>>([])
  const [groupCounts, setGroupCounts] = useState<number[]>([])

  useEffect(() => {
    const postGroups: IndexedObject<PostGroupData> = props.posts.reduce((acc, post, index) => {
      if (!acc[post.weekday]) {
        acc[post.weekday] = {
          posts: [post],
          key: post.weekday,
          weekday: post.weekday,
          postsStartIndex: index
        }
      } else {
        acc[post.weekday].posts.push(post)
      }
      return acc
    }, {} as IndexedObject<PostGroupData>)

    setGroups(Object.values(postGroups))
    setGroupCounts(Object.values(postGroups).map(group => group.posts.length))
  }, [props.posts])

  const showGrouped = useMemo(() => !props.sortBy || props.sortBy === PostedSortBy.Time, [props.sortBy])

  return (
    <div className={`${styles.wrapper} ${props.className || ''}`} data-test="post-list">
      <div className={`${styles.actions} ${styles.posted} ${props.actionsClassName || ''}`}>
        {props.posts.length > 0 && (
          <BulkPostActions {...props.bulkActionsProps} />
        )}
      </div>
      <ScrollListener
        scrollElement={scrollElementRef.current}
        emitTreshold={SCROLL_TRESHOLD}
        onScroll={props.onLoadNextPage}
      >
        <div>
          {!showGrouped && (
            <Virtuoso
              customScrollParent={scrollElementRef.current}
              increaseViewportBy={1000}
              overscan={1000}
              totalCount={props.posts.length}
              components={{ ScrollSeekPlaceholder: PostedPostPlaceholder }}
              scrollSeekConfiguration={{
                /* eslint-disable no-magic-numbers */
                enter: (velocity) => Math.abs(velocity) > 800,
                exit: (velocity) => Math.abs(velocity) < 80
                /* eslint-enable no-magic-numbers */
              }}
              itemContent={index => {
                const post = props.posts[index]
                return (
                  <PostedPost
                    key={post.id}
                    post={post as IPostedPost}
                    onRequeue={props.onRequeuePosted}
                    onRepost={props.onRepost}
                    onCopy={props.onCopy}
                    onToggleSelected={props.onTogglePostSelected}
                    selected={props.selectedPosts === 'all' || !!props.selectedPosts[post.id]}
                    withDate={props.sortBy && props.sortBy !== PostedSortBy.Time}
                    onMediaPreviewBtnClick={props.onMediaPreviewBtnClick}
                    onShareNow={props.onShareNow}
                  />
                )
              }}
            />
          )}
          {showGrouped && (
            <GroupedVirtuoso
              increaseViewportBy={1000}
              overscan={1000}
              customScrollParent={scrollElementRef.current}
              groupCounts={groupCounts}
              groupContent={(index) => <PostGroupHeader title={groups[index].weekday} />}
              itemContent={(flatIndex, groupIndex) => {
                const postIndex = flatIndex - groups[groupIndex].postsStartIndex
                const post = groups[groupIndex].posts[postIndex]
                return (
                  <PostedPost
                    key={post.id}
                    post={post as IPostedPost}
                    selected={props.selectedPosts === 'all' || !!props.selectedPosts[post.id]}
                    onRequeue={props.onRequeuePosted}
                    onRepost={props.onRepost}
                    onCopy={props.onCopy}
                    onToggleSelected={props.onTogglePostSelected}
                    withDate={props.sortBy && props.sortBy !== PostedSortBy.Time}
                    onMediaPreviewBtnClick={props.onMediaPreviewBtnClick}
                    onShareNow={props.onShareNow}
                  />
                )
              }}
            />
          )}
        </div>
      </ScrollListener>
    </div>
  )
}

function PostedPostPlaceholder(props: ScrollSeekPlaceholderProps) {
  return (
    <ScrollSeekPlaceholder
      {...props}
      className={styles.placeholder}
      containerClassName={styles.placeholderContainer}
    />
  )
}

export default PostedPostsList
